import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Loader2, AlertCircle, BarChart2 } from 'lucide-react';
import ParentNav from '@/components/ParentNav';
import DaysInsightsReport from '@/components/DaysInsightsReport';
import MonthlyInsightExtras from '@/components/MonthlyInsightExtras';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ParentMonthlyReport() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('parent_token');
    if (!token) { navigate('/parent/login'); return; }
    axios.get(`${BACKEND_URL}/api/parent/monthly-reports/${reportId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => setReport(r.data.report))
      .catch(() => setError('Could not load this report.'))
      .finally(() => setLoading(false));
  }, [reportId, navigate]);

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-rose-50">
      <ParentNav />
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-10 h-10 animate-spin text-orange-500" />
      </div>
    </div>
  );

  if (error || !report) return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-rose-50">
      <ParentNav />
      <div className="max-w-2xl mx-auto px-4 pt-24 text-center">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <p className="text-gray-600">{error || 'Report not found.'}</p>
        <Button className="mt-4" variant="outline" onClick={() => navigate('/parent/dashboard')}>
          Back to Dashboard
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-rose-50">
      <ParentNav />

      <div className="pt-20 pb-12 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Header — matches Daily Insights style */}
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/parent/monthly-reports')}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <BarChart2 className="w-6 h-6 text-orange-500" />
                  {report.child_name}
                </h1>
                <p className="text-gray-600 text-sm">
                  {report.month_label} · {report.report_count} session{report.report_count !== 1 ? 's' : ''}
                  {report.submitted_at && ` · Shared ${new Date(report.submitted_at).toLocaleDateString()}`}
                </p>
              </div>
            </div>
          </div>

          {/* Report Content */}
          {report.content
            ? (
              <>
                <DaysInsightsReport data={report.content} title="Month's Insights" />
                <MonthlyInsightExtras data={report.content} />
              </>
            )
            : (
              <div className="flex items-center justify-center py-16">
                <AlertCircle className="w-8 h-8 text-gray-400 mr-3" />
                <p className="text-gray-500">Report content is not available.</p>
              </div>
            )
          }
        </div>
      </div>
    </div>
  );
}
